/**
 * Settings page — tabbed reference data management.
 *
 * Renders a tab shell that delegates to self-contained panel components
 * for Machine Types, Parts, Categories, Tools, and Safety Warnings.
 *
 * Requirements: 10.1, 10.2, 10.3, 10.4, 10.5, 10.6
 */

import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { PageContainer } from '../../components/shared';
import MachineTypesPanel from './MachineTypesPanel';
import PartTypesPanel from './PartTypesPanel';
import CategoriesPanel from './CategoriesPanel';
import ToolsPanel from './ToolsPanel';
import SafetyWarningsPanel from './SafetyWarningsPanel';

// ─── Tab Panel Helper ────────────────────────────────────────────────────────

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  if (value !== index) return null;
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', pt: 2 }}>
      {children}
    </Box>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────

const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <PageContainer title="Settings">
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(_e, newValue: number) => setActiveTab(newValue)}
          aria-label="Settings tabs"
        >
          <Tab label="Machine Types" id="settings-tab-0" aria-controls="settings-tabpanel-0" />
          <Tab label="Part Types" id="settings-tab-1" aria-controls="settings-tabpanel-1" />
          <Tab label="Categories" id="settings-tab-2" aria-controls="settings-tabpanel-2" />
          <Tab label="Tools" id="settings-tab-3" aria-controls="settings-tabpanel-3" />
          <Tab label="Safety Warnings" id="settings-tab-4" aria-controls="settings-tabpanel-4" />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <MachineTypesPanel />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <PartTypesPanel />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <CategoriesPanel />
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <ToolsPanel />
      </TabPanel>

      <TabPanel value={activeTab} index={4}>
        <SafetyWarningsPanel />
      </TabPanel>
    </PageContainer>
  );
};

export default SettingsPage;
